import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Autocomplete, useMediaQuery } from '@mui/material';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import { useToast } from '../../providers/toaster-provider';
import countriesData from '../../files/json/countries.json';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';

export interface InvoiceClientDetails {
    company: string;
    recipient: string;
    streetAddress1: string;
    streetAddress2?: string;
    suburb: string;
    state: string;
    postalCode: string;
    country: string;
    phone?: string;
    fax?: string;
    email?: string;
}

interface EditInvoiceClientProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: InvoiceClientDetails) => void;
    initialData?: InvoiceClientDetails;
    isCompanyEditable?: boolean;
    isEdit?: boolean;
}

const EditInvoiceClient: React.FC<EditInvoiceClientProps> = ({ 
    open, 
    onClose, 
    onSave,
    initialData,
    isCompanyEditable = true,
    isEdit = false
}) => {
    const { showError } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form state
    const [recipient, setRecipient] = useState(initialData?.recipient || '');
    const [streetAddress1, setStreetAddress1] = useState(initialData?.streetAddress1 || '');
    const [streetAddress2, setStreetAddress2] = useState(initialData?.streetAddress2 || '');
    const [suburb, setSuburb] = useState(initialData?.suburb || '');
    const [state, setState] = useState(initialData?.state || '');
    const [postalCode, setPostalCode] = useState(initialData?.postalCode || '');
    const [country, setCountry] = useState(initialData?.country || '');
    const [phone, setPhone] = useState(initialData?.phone || '');
    const [fax, setFax] = useState(initialData?.fax || '');
    const [email, setEmail] = useState(initialData?.email || '');
    
    const [errors, setErrors] = useState<{
        recipient?: string,
        streetAddress1?: string,
        suburb?: string,
        state?: string,
        postalCode?: string,
        country?: string,
        phone?: string,
        email?: string
    }>({});

    // Dynamically generate country options from JSON
    const countryOptions: DropdownOption[] = useMemo(() => {
        return countriesData.map((country: any) => ({
            value: country.code2,
            label: country.name
        }));
    }, []);

    // Dynamically generate states/provinces map from JSON - only for US and Switzerland
    const countriesWithStates: Record<string, DropdownOption[]> = useMemo(() => {
        const map: Record<string, DropdownOption[]> = {};
        const allowedCountries = ['US', 'CH']; // Only US and Switzerland
        
        countriesData.forEach((country: any) => {
            if (allowedCountries.includes(country.code2) && country.states && country.states.length > 0) {
                map[country.code2] = country.states.map((state: any) => ({
                    value: state.code,
                    label: state.name
                }));
            }
        });
        return map;
    }, []);

    // Get state options for the selected country
    const stateOptions = country && countriesWithStates[country] ? countriesWithStates[country] : [];
    const hasStates = stateOptions.length > 0;

    // Reset form when country changes
    React.useEffect(() => {
        setState('');
    }, [country]);

    const validate = () => {
        const newErrors: typeof errors = {};
        
        // Required fields
        if (!recipient.trim()) newErrors.recipient = 'Recipient name is required';
        if (!streetAddress1.trim()) newErrors.streetAddress1 = 'Street address is required';
        if (!suburb.trim()) newErrors.suburb = 'Suburb is required';
        if (hasStates && !state.trim()) newErrors.state = 'State is required';
        if (!postalCode.trim()) newErrors.postalCode = 'Postal code is required';
        if (!country.trim()) newErrors.country = 'Country is required';

        // Email validation
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Phone validation (basic)
        if (phone && !/^[\d\s\-+()]+$/.test(phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            showError('Please fill in all required fields');
            return;
        }

        const clientData: InvoiceClientDetails = {
            company: initialData?.company || '',
            recipient: recipient.trim(),
            streetAddress1: streetAddress1.trim(),
            streetAddress2: streetAddress2.trim() || undefined,
            suburb: suburb.trim(),
            state,
            postalCode: postalCode.trim(),
            country,
            phone: phone.trim() || undefined,
            fax: fax.trim() || undefined,
            email: email.trim() || undefined,
        };

        onSave(clientData);
    };

    const handleClose = () => {
        // Reset form
        if (initialData) {
            setRecipient(initialData.recipient || '');
            setStreetAddress1(initialData.streetAddress1 || '');
            setStreetAddress2(initialData.streetAddress2 || '');
            setSuburb(initialData.suburb || '');
            setState(initialData.state || '');
            setPostalCode(initialData.postalCode || '');
            setCountry(initialData.country || '');
            setPhone(initialData.phone || '');
            setFax(initialData.fax || '');
            setEmail(initialData.email || '');
        } else {
            setRecipient('');
            setStreetAddress1('');
            setStreetAddress2('');
            setSuburb('');
            setState('');
            setPostalCode('');
            setCountry('');
            setPhone('');
            setFax('');
            setEmail('');
        }
        setErrors({});
        onClose();
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 0.5 }}>
                Edit Client Details
            </DialogTitle>
            <Box sx={{ px: 2.5, pb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: '#999999', fontWeight: 400 }}>
                    Editing company details here will only apply to this invoice.
                </Typography>
            </Box>
            <DialogContent sx={{ pt: 1.5 }}>
                {/* Recipient */}
                <Box mb={1.5}>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                        Recipient <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Recipient Name"
                        value={recipient}
                        onChange={e => setRecipient(e.target.value)}
                        error={!!errors.recipient}
                        helperText={errors.recipient}
                        disabled={isEdit && !isCompanyEditable}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                {/* Street Address Line 1 */}
                <Box mb={1.5}>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                        Street Address Line 1 <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter PO Box or Street Address"
                        value={streetAddress1}
                        onChange={e => setStreetAddress1(e.target.value)}
                        error={!!errors.streetAddress1}
                        helperText={errors.streetAddress1}
                        disabled={isEdit && !isCompanyEditable}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                {/* Street Address Line 2 */}
                <Box mb={1.5}>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                        Street Address Line 2
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Unit, Suite, or Building name (optional)"
                        value={streetAddress2}
                        onChange={e => setStreetAddress2(e.target.value)}
                        disabled={isEdit && !isCompanyEditable}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                {/* Suburb and State */}
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1.5} mb={1.5}>
                    <Box flex={hasStates ? 1 : 1} sx={{ width: '100%' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                            Suburb <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Suburb"
                            value={suburb}
                            onChange={e => setSuburb(e.target.value)}
                            error={!!errors.suburb}
                            helperText={errors.suburb}
                            disabled={isEdit && !isCompanyEditable}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    {hasStates && (
                        <Box flex={1}>
                            <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                                State <span style={{ color: '#F44336' }}>*</span>
                            </Typography>
                            <Autocomplete
                                options={stateOptions}
                                getOptionLabel={(option) => option.label}
                                value={stateOptions.find(s => s.value === state) || null}
                                onChange={(e, value) => setState(value?.value || '')}
                                disabled={isEdit && !isCompanyEditable}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select state"
                                        error={!!errors.state}
                                        helperText={errors.state}
                                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                                    />
                                )}
                                noOptionsText="No states found"
                            />
                        </Box>
                    )}
                </Box>

                {/* Postal Code and Country */}
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1.5} mb={1.5}>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                            Postal Code <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Postcode"
                            value={postalCode}
                            onChange={e => setPostalCode(e.target.value)}
                            error={!!errors.postalCode}
                            helperText={errors.postalCode}
                            disabled={isEdit && !isCompanyEditable}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                            Country <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <Autocomplete
                            options={countryOptions}
                            getOptionLabel={(option) => option.label}
                            value={countryOptions.find(c => c.value === country) || null}
                            onChange={(e, value) => setCountry(value?.value || '')}
                            disabled={isEdit && !isCompanyEditable}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select country"
                                    error={!!errors.country}
                                    helperText={errors.country}
                                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                                />
                            )}
                            noOptionsText="No countries found"
                        />
                    </Box>
                </Box>

                {/* Phone and Fax */}
                <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1.5} mb={1.5}>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                            Phone
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter mobile or landline number"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            disabled={isEdit && !isCompanyEditable}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                            Fax
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter fax number (if applicable)"
                            value={fax}
                            onChange={e => setFax(e.target.value)}
                            disabled={isEdit && !isCompanyEditable}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                </Box>

                {/* Email */}
                <Box mb={1.5}>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.4, color: 'var(--color-text-muted)' }}>
                        Email
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={isEdit && !isCompanyEditable}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 2.5, pb: 1.5 }}>
                <Button 
                    onClick={handleClose} 
                    variant="contained" 
                    size="small"
                    sx={{ 
                        background: 'var(--color-container-background)', 
                        color: 'var(--color-text-muted)', 
                        boxShadow: 'none', 
                        borderRadius: 1.5, 
                        fontWeight: 600, 
                        fontSize: 13,
                        px: 2,
                        '&:hover': { background: 'var(--color-container-background)' } 
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    size="small"
                    sx={{ 
                        background: 'var(--color-primary)', 
                        color: '#fff', 
                        boxShadow: 'none', 
                        borderRadius: 1.5, 
                        fontWeight: 600, 
                        fontSize: 13,
                        px: 2.5,
                        '&:hover': { background: 'var(--color-primary)' } 
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}>
            {content}
        </Dialog>
    );
};

export default EditInvoiceClient;
